import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Bell, X, Check, CheckCheck, AlertCircle, AlertTriangle, Info, CheckCircle } from 'lucide-react';
import { useNotifications, Notification, NotificationSeverity } from '../contexts/NotificationContext';
import { formatDateTime } from '../utils/dateFormatter';
import './NotificationCenter.css';

export default function NotificationCenter() {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAll,
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const centerRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const [positionStyle, setPositionStyle] = useState<React.CSSProperties>({});

  // Calcular posición al abrir
  useEffect(() => {
    if (isOpen && bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 768;
      
      if (isMobile) {
        setPositionStyle({ top: 0, right: 0, left: 0, width: 'auto' });
      } else {
        // Determinar si estamos en el sidebar (izquierda) o header (derecha)
        const isLeftSidebar = rect.left < window.innerWidth / 2;

        if (isLeftSidebar) {
          // Alinear a la izquierda de la campana
          setPositionStyle({
            top: rect.bottom + 10,
            left: rect.left,
            position: 'fixed'
          });
        } else {
          // Alinear a la derecha de la campana (comportamiento original)
          setPositionStyle({ 
            top: rect.bottom + 10, 
            right: window.innerWidth - rect.right,
            position: 'fixed'
          });
        }
      }
    }
  }, [isOpen]);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (centerRef.current && !centerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const getSeverityIcon = (severity: NotificationSeverity) => {
    switch (severity) {
      case 'error':
        return <AlertCircle size={18} />;
      case 'warning':
        return <AlertTriangle size={18} />;
      case 'success':
        return <CheckCircle size={18} />;
      case 'info':
      default:
        return <Info size={18} />;
    }
  };

  const getSeverityColor = (severity: NotificationSeverity) => {
    switch (severity) {
      case 'error':
        return 'var(--error)';
      case 'warning':
        return 'var(--warning)';
      case 'success':
        return 'var(--success)';
      case 'info':
      default:
        return 'var(--primary)';
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.actionUrl) {
      window.location.href = notification.actionUrl;
    }
  };

  return (
    <>
      <button
        className="notification-bell"
        ref={bellRef}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notificaciones"
        title="Notificaciones"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && createPortal(
        <>
          <div className="notification-overlay" onClick={() => setIsOpen(false)} />
          <div 
            className="notification-center" 
            ref={centerRef}
            style={positionStyle}
          >
            <div className="notification-center-header">
              <h3>Notificaciones</h3>
              <div className="notification-center-actions">
                {unreadCount > 0 && (
                  <button
                    className="notification-action-btn"
                    onClick={markAllAsRead}
                    title="Marcar todas como leídas"
                  >
                    <CheckCheck size={16} />
                  </button>
                )}
                {notifications.length > 0 && (
                  <button
                    className="notification-action-btn"
                    onClick={clearAll}
                    title="Limpiar todas"
                  >
                    <X size={16} />
                  </button>
                )}
                <button
                  className="notification-action-btn"
                  onClick={() => setIsOpen(false)}
                  title="Cerrar"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="notification-center-body">
              {notifications.length === 0 ? (
                <div className="notification-empty">
                  <Bell size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                  <p>No hay notificaciones</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`notification-item ${notification.read ? 'is-read' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div
                      className="notification-item-icon"
                      style={{ color: getSeverityColor(notification.severity) }}
                    >
                      {getSeverityIcon(notification.severity)}
                    </div>
                    <div className="notification-item-content">
                      <div className="notification-item-header">
                        <h4 className="notification-item-title">{notification.title}</h4>
                        {!notification.read && <span className="notification-item-dot" />}
                      </div>
                      <p className="notification-item-message">{notification.message}</p>
                      <span className="notification-item-time">
                        {formatDateTime(notification.timestamp.toISOString())}
                      </span>
                    </div>
                    <div className="notification-item-actions">
                      {!notification.read && (
                        <button
                          className="notification-item-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                          title="Marcar como leída"
                        >
                          <Check size={14} />
                        </button>
                      )}
                      <button
                        className="notification-item-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNotification(notification.id);
                        }}
                        title="Eliminar"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

